<template>
  <div>
    <transition appear name="loading-bar">
      <loading-bar v-show="pending" />
    </transition>

    <b-card-header class="text-right">
      <b-button @click="generatePdf">
        <fa class="mr-1" icon="file-pdf" />
        {{ $t('Long version as PDF') }}
      </b-button>

      <b-dropdown :text="$t('Short version')">
        <b-dropdown-item @click="generateDoc">
          <fa class="mr-1" icon="file-alt" />
          {{ $t('Short version as RTF') }}
        </b-dropdown-item>
        <b-dropdown-item @click="generatePdf(true)">
          <fa class="mr-1" icon="file-pdf" />
          {{ $t('Short version as PDF') }}
        </b-dropdown-item>
      </b-dropdown>
    </b-card-header>

    <b-card-header
        class="mb-0"
        header-bg-variant="transparent"
    >
      <b-form @submit.prevent="fetchData">
        <b-form-group
            :label="$t('Disability')"
            label-for="disability_id"
        >
          <b-form-row>
            <b-col>
              <b-input-group>
                <disabilities-select
                    id="disability_id"
                    v-model="disability"
                    :resource="`disabilities`"
                    :resource-params="{project_id: projectId}"
                    class="w-auto"
                    @input="onDisabilityInput"
                />

                <b-input-group-append>
                  <b-button @click="resetDisability">
                    <fa :title="$t('Clear')" icon="times" />
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </b-col>
            <b-col cols="auto">
              <b-form-radio-group
                  id="btn-radios-1"
                  v-model="filterByStatusSelected"
                  :options="filterByStatusOptions"
                  name="radios-btn-default"
                  buttons
                  @input="onStatusInput"
              />
            </b-col>
          </b-form-row>
        </b-form-group>
      </b-form>
    </b-card-header>

    <b-card-body>
      <no-results-found v-if="!pending && !items.length" class="mb-0" />

      <b-card
          v-for="component in items"
          :key="`component_${component.id}`"
          no-body
      >
        <b-card-header class="p-1" role="tab">
          <b-button
              v-b-toggle="`component_${component.id}_collapse`"
              block
              class="text-left"
              variant="default"
          >
            {{ component.name }}
          </b-button>
        </b-card-header>

        <b-collapse :id="`component_${component.id}_collapse`">
          <b-card
              v-for="floor in component.children"
              :key="`floor_${floor.id}`"
              no-body
          >
            <b-card-header class="p-1" role="tab">
              <b-button
                  v-b-toggle="`floor_${floor.id}_collapse`"
                  block
                  class="text-left"
                  variant="default"
              >
                {{ floor.name }}
              </b-button>
            </b-card-header>

            <b-collapse :id="`floor_${floor.id}_collapse`">
              <b-card
                  v-for="room in floor.children"
                  :key="`room_${room.id}`"
                  no-body
              >
                <b-card-header class="p-1 d-flex">
                  <b-link
                      class="px-3 py-2"
                      :to="{name: 'projects.components.floors.rooms.show', params: { projectId, componentId: component.id, floorId: floor.id, roomId: room.id}}"
                  >
                    {{ $t('Room') }}: {{ room.name }}
                  </b-link>

                  <b-button
                      size="sm"
                      class="ml-auto"
                      @click.prevent="showRoom(room.id)"
                  >
                    {{ $t('Load data') }}
                  </b-button>
                </b-card-header>

                <room-disabilities v-if="loadedRooms.includes(room.id)" :room-id="room.id" :status="params.status"/>
              </b-card>
            </b-collapse>
          </b-card>
        </b-collapse>
      </b-card>
    </b-card-body>
  </div>
</template>

<script>
import fetchData from '@/mixins/fetchData';
import DisabilitiesSelect from "@/components/selects/ResourceSelect";
import RoomDisabilities from "@/components/rooms/RoomDisabilities";

export default {
  name: 'ProjectDisabilities',
  components: {RoomDisabilities, DisabilitiesSelect},
  mixins: [fetchData],
  props: {
    projectId: {
      type: [String, Number],
      required: true,
    },
  },

  data() {
    return {
      disability: null,
      visibleRooms: {},
      loadedRooms: [],
      filterByStatusSelected: null,
      filterByStatusOptions: [
        {
          text: this.$t('disability_statuses.all'),
          value: null
        },
        {
          text: this.$t('disability_statuses.closed'),
          value: 'closed'
        },
        {
          text: this.$t('disability_statuses.open'),
          value: 'open'
        },
      ]
    }
  },

  computed: {
    apiUrl() {
      return `/api/projects/${this.projectId}/disabilities`;
    },
  },

  created() {
    this.$root.$on('disabilities-assigned', this.fetchData);
    this.$root.$on('disabilities-dropped', this.fetchData);
  },

  methods: {
    onDisabilityInput($event) {
      this.$set(this.params, 'disability_id', $event ? $event.id : null)
    },
    onStatusInput($event) {
      this.$set(this.params, 'status', $event)
    },

    resetDisability() {
      this.disability = null;
      this.$set(this.params, 'disability_id', null)
    },

    async generatePdf(shortVersion = false) {
      let message = ''
      if (shortVersion) {
        let response = await this.axios.get(this.apiUrl + '/pdf-short').then(({data}) => data);
        message = response.message
      } else {
        let response = await this.axios.get(this.apiUrl + '/pdf').then(({data}) => data);
        message = response.message
      }

      this.$bvToast.toast(message, {
        title: this.$t('Success'),
        solid: true,
        variant: 'success'
      })
    },

    async generateDoc() {
      let {message} = await this.axios.get(this.apiUrl + '/doc').then(({data}) => data);

      this.$bvToast.toast(message, {
        title: this.$t('Success'),
        solid: true,
        variant: 'success'
      })
    },
    showRoom(roomId) {
      if (!this.loadedRooms.includes(roomId)) {
        this.loadedRooms.push(roomId);
      }
    }
  },
};
</script>

<style lang="scss" scoped>
@import 'resources/sass/variables';

.card {
  margin-top: $spacer * 0.5;
}

.card .card {
  margin-left: $spacer * 0.5;
  margin-right: $card-border-width * -1;
}
</style>
